/** Cache des buffers décodés (évite de re-fetch à chaque trappe). */
const bufferCache = new Map();
const loadingPromises = new Map();

let sharedAudioCtx = null;
let gameSampleMixHook = null;
/** Source longue (chrono) — stoppable entre deux questions. */
let activeStoppableSource = null;

export function stopActiveStoppableSample() {
  if (!activeStoppableSource) return;
  try {
    activeStoppableSource.stop(0);
  } catch {
    /* ignore */
  }
  activeStoppableSource = null;
}

export function getSharedAudioContext() {
  return getAudioContext();
}

function getAudioContext() {
  if (!sharedAudioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    sharedAudioCtx = new Ctor();
  }
  return sharedAudioCtx;
}

/** Débloque l'AudioContext (politique autoplay des navigateurs). */
export function unlockAudioContext() {
  const ctx = getAudioContext();
  if (!ctx) return Promise.resolve(false);
  if (ctx.state === "running") return Promise.resolve(true);
  return ctx.resume().then(() => ctx.state === "running").catch(() => false);
}

async function loadBuffer(url) {
  if (bufferCache.has(url)) return bufferCache.get(url);
  if (loadingPromises.has(url)) return loadingPromises.get(url);

  const promise = (async () => {
    const ctx = getAudioContext();
    if (!ctx) throw new Error("Web Audio indisponible");
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Audio fetch failed: ${url}`);
    const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    bufferCache.set(url, buffer);
    return buffer;
  })();

  loadingPromises.set(url, promise);
  try {
    return await promise;
  } finally {
    loadingPromises.delete(url);
  }
}

/** Précharge les samples en mémoire (appeler au montage de Play). */
export async function preloadAudioSamples(urls) {
  await unlockAudioContext();
  const unique = [...new Set(urls.filter(Boolean))];
  await Promise.all(unique.map((url) => loadBuffer(url).catch(() => null)));
}

/** Enregistrement composite : branche les samples Web Audio dans le mix vidéo. */
export function registerGameSampleMixHook(hook) {
  gameSampleMixHook = typeof hook === "function" ? hook : null;
}

function isMixRoutingActive() {
  return typeof gameSampleMixHook === "function";
}

/** Haut-parleurs et/ou piste enregistrement (gains indépendants). */
function routeGameSample(ctx, source, gain, { toSpeakers = true } = {}) {
  if (toSpeakers) {
    const speakerGain = ctx.createGain();
    speakerGain.gain.value = gain;
    source.connect(speakerGain);
    speakerGain.connect(ctx.destination);
  }

  if (isMixRoutingActive()) {
    const recordGain = ctx.createGain();
    recordGain.gain.value = gain;
    source.connect(recordGain);
    gameSampleMixHook(recordGain);
  }
}

async function playHtmlFallback(url, volume = 1) {
  const clip = new Audio(url);
  clip.preload = "auto";
  clip.volume = Math.min(1, Math.max(0, volume));
  await clip.play().catch(() => {});
}

/**
 * Joue un sample court (push, Open, etc.) via Web Audio avec gain > 1.
 * Repli HTMLAudioElement si Web Audio indisponible ou en échec.
 * @param {{ toSpeakers?: boolean }} [options] — toSpeakers:false = silencieux candidat, toujours dans le mix vidéo.
 */
export async function playGameSample(url, gain = 1, { toSpeakers = true } = {}) {
  const ctx = getAudioContext();
  if (!ctx) {
    if (toSpeakers) await playHtmlFallback(url, gain);
    return;
  }

  await unlockAudioContext();

  try {
    const buffer = await loadBuffer(url);
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    routeGameSample(ctx, source, gain, { toSpeakers });
    source.start(0);
  } catch {
    if (toSpeakers) await playHtmlFallback(url, gain);
  }
}

/** Comme playGameSample, résout à la fin de la lecture. */
export async function playGameSampleWithEnd(url, gain = 1, { stoppable = false, toSpeakers = true } = {}) {
  const ctx = getAudioContext();
  if (!ctx) {
    if (toSpeakers) await playHtmlFallback(url, gain);
    return;
  }

  await unlockAudioContext();

  try {
    const buffer = await loadBuffer(url);
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    await new Promise((resolve) => {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      routeGameSample(ctx, source, gain, { toSpeakers });
      const finish = () => {
        if (stoppable && activeStoppableSource === source) {
          activeStoppableSource = null;
        }
        resolve();
      };
      source.onended = finish;
      if (stoppable) {
        stopActiveStoppableSample();
        activeStoppableSource = source;
      }
      source.start(0);
    });
  } catch {
    if (toSpeakers) await playHtmlFallback(url, gain);
  }
}

/** @deprecated Utiliser playGameSample */
export async function playBoostedSample(url, gain = 2.5) {
  return playGameSample(url, gain);
}

export function playClonedSample(audioEl) {
  if (!audioEl) return;
  const clip = audioEl.cloneNode(true);
  clip.volume = 1;
  clip.play().catch(() => {});
}

/** Attend que l'élément <audio> soit prêt (chrono, valid, etc.). */
export function waitAudioElementReady(audioEl) {
  if (!audioEl) return Promise.resolve(false);
  if (audioEl.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const finish = (ok) => {
      audioEl.removeEventListener("canplaythrough", onReady);
      audioEl.removeEventListener("error", onError);
      resolve(ok);
    };
    const onReady = () => finish(audioEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA);
    const onError = () => finish(false);
    audioEl.addEventListener("canplaythrough", onReady, { once: true });
    audioEl.addEventListener("error", onError, { once: true });
    audioEl.load();
  });
}
